import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  DocumentIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  ArrowPathIcon,
  Bars3Icon,
  Squares2X2Icon,
  UserIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import { leadsAPI, leadStagesAPI, usersAPI, followupsAPI, companiesAPI } from '../../services/api';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const LeadList = ({ onCreateLead, onEditLead, onViewLead, onLeadUpdate, onLeadDelete, onDeleteLead, onBulkDelete, refreshTrigger }) => {
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customFieldFilters, setCustomFieldFilters] = useState({});
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedAssignedTo, setSelectedAssignedTo] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Date filter states
  const [dateFilter, setDateFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [showCostAnalysis, setShowCostAnalysis] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Set default view to kanban on mobile devices
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768 ? 'kanban' : 'table';
    }
    return 'table';
  });
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [quickFollowupOpen, setQuickFollowupOpen] = useState(false);
  const [quickFollowupLead, setQuickFollowupLead] = useState(null);
  const [quickFollowupData, setQuickFollowupData] = useState({ note: '', remindAt: '' });
  const [costAnalysis, setCostAnalysis] = useState({
    totalCost: 0,
    wonCost: 0,
    lostCost: 0,
    inProcessCost: 0,
    totalLeads: 0
  });

  const { companyData } = useCompany();
  const { hasRole } = useAuth();

  // Filter options
  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'high', label: 'High', color: 'bg-red-100 text-red-800' }
  ];

  const sourceOptions = [
    'Website', 'Referral', 'Social Media', 'Email Campaign', 'Cold Call',
    'Trade Show', 'Direct Mail', 'Online Ads', 'Partnership', 'Other'
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'converted', label: 'Converted' }
  ];

  useEffect(() => {
    fetchLeads();
    fetchLeadStages();
    fetchUsers();
    fetchCustomFields();
    fetchCostAnalysis();
  }, [searchTerm, selectedStage, selectedPriority, selectedAssignedTo, selectedSource, selectedStatus, dateFilter, startDate, endDate, customFieldFilters, refreshTrigger]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm || undefined,
        stage: selectedStage || undefined,
        priority: selectedPriority || undefined,
        assignedTo: selectedAssignedTo || undefined,
        source: selectedSource || undefined,
        status: selectedStatus || undefined,
        sortBy,
        sortOrder,
        dateFilter: dateFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        ...customFieldFilters
      };

      const response = await leadsAPI.getAll(params);
      setLeads(response.data.leads || []);
      setTotalLeads(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadStages = async () => {
    try {
      const response = await leadStagesAPI.getAll();
      setLeadStages(response.data.leadStages || []);
    } catch (error) {
      console.error('Error fetching lead stages:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await companiesAPI.getCustomFields('lead');
      setCustomFields(response.data.customFields || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const handleDeleteLead = (lead) => {
    onDeleteLead(lead);
  };

  const handleBulkDelete = () => {
    onBulkDelete();
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      await leadsAPI.updateStage(leadId, newStage);
      toast.success('Lead stage updated');
      fetchLeads();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      console.error('Error updating lead stage:', error);
      toast.error('Failed to update lead stage');
    }
  };

  const handleAssignmentChange = async (leadId, assignedTo) => {
    try {
      await leadsAPI.updateAssignment(leadId, assignedTo);
      toast.success('Lead assignment updated');
      fetchLeads();
      if (onLeadUpdate) onLeadUpdate();
    } catch (error) {
      console.error('Error updating lead assignment:', error);
      toast.error('Failed to update lead assignment');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStage('');
    setSelectedPriority('');
    setSelectedAssignedTo('');
    setSelectedSource('');
    setSelectedStatus('active');
    setDateFilter('');
    setStartDate('');
    setEndDate('');
    setShowCustomDatePicker(false);
    setCustomFieldFilters({});
    setCurrentPage(1);
  };

  const handleCustomFieldFilterChange = (fieldName, value) => {
    setCustomFieldFilters(prev => ({
      ...prev,
      [fieldName]: value || undefined
    }));
    setCurrentPage(1);
  };

  const renderCustomFieldFilter = (field) => {
    const fieldName = field.fieldName || 'unknown_field';
    const value = customFieldFilters[fieldName] || '';

    switch (field.fieldType) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldFilterChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            placeholder={`Filter by ${field.fieldName || 'field'}`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldFilterChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            placeholder={`Filter by ${field.fieldName || 'field'}`}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldFilterChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldFilterChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          >
            <option value="">All {field.fieldName || 'field'}</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <select
            value={value}
            onChange={(e) => handleCustomFieldFilterChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          >
            <option value="">All {field.fieldName || 'field'}</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldFilterChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            placeholder={`Filter by ${field.fieldName || 'field'}`}
          />
        );
    }
  };

  const getStageColor = (stage) => {
    const id = stage && (typeof stage === 'string' ? stage : (stage._id || stage));
    const stageConfig = leadStages.find(s => String(s._id) === String(id));
    return stageConfig?.color || '#6B7280';
  };

  const getPriorityColor = (priority) => {
    const priorityConfig = priorityOptions.find(p => p.value === priority);
    return priorityConfig?.color || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const fetchCostAnalysis = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        stage: selectedStage || undefined,
        priority: selectedPriority || undefined,
        assignedTo: selectedAssignedTo || undefined,
        source: selectedSource || undefined,
        status: selectedStatus || undefined,
        dateFilter: dateFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        ...customFieldFilters
      };

      const response = await leadsAPI.getCostAnalysis(params);
      setCostAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching cost analysis:', error);
    }
  };

  const renderTableView = () => (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      {leads.length === 0 ? (
        <div className="text-center py-12 px-4">
          <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
            <UserPlusIcon className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            {searchTerm || selectedStage || selectedPriority || selectedAssignedTo || selectedSource || selectedStatus !== 'active'
              ? 'Try adjusting your filters or search terms to find more leads.'
              : 'Get started by creating your first lead to track potential customers and opportunities.'
            }
          </p>
          <button
            onClick={onCreateLead}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create First Lead
          </button>
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full divide-y divide-white/30 min-w-[1200px]">
            <thead className="bg-gradient-to-r from-white/60 to-gray-50/60 backdrop-blur-sm">
              <tr>
                <th className="w-12 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLeads(leads.map(lead => lead._id));
                      } else {
                        setSelectedLeads([]);
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="min-w-0 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  Lead Details
                </th>
                <th className="hidden sm:table-cell min-w-0 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  Contact & Company
                </th>
                <th className="w-20 sm:w-24 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  Value
                </th>
                <th className="w-24 sm:w-32 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  Status & Priority
                </th>
                <th className="hidden md:table-cell w-48 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  Assignment
                </th>
                <th className="hidden lg:table-cell min-w-0 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  Industry Fields
                </th>
                <th className="w-40 px-3 sm:px-4 py-3 sm:py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white/40 backdrop-blur-sm divide-y divide-white/20">
              {leads.map((lead) => (
                <motion.tr
                  key={lead._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-white/60 transition-all duration-200 group"
                >
                  <td className="w-12 px-3 sm:px-4 py-4 sm:py-6 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeads([...selectedLeads, lead._id]);
                        } else {
                          setSelectedLeads(selectedLeads.filter(id => id !== lead._id));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="min-w-0 px-3 sm:px-4 py-4 sm:py-6">
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-10 sm:h-12 w-10 sm:w-12 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-sm sm:text-lg">
                            {(lead.customer?.firstName || 'N')[0].toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2">
                          <h3 className="text-sm font-bold text-gray-900 truncate">
                            {lead.customer?.firstName || 'N/A'} {lead.customer?.lastName || ''}
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {lead.leadno || 'N/A'}
                            </span>
                          </h3>
                          {lead.customer?.jobTitle && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 sm:mt-0 mt-1 self-start">
                              {lead.customer.jobTitle}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mt-1">
                          <span className="text-sm text-gray-600 truncate">
                            {lead.customer?.companyName || 'No Company'}
                          </span>
                          {lead.source && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 sm:mt-0 mt-1 self-start">
                              {lead.source}
                            </span>
                          )}
                        </div>
                        {/* Mobile-only contact info */}
                        <div className="sm:hidden mt-2 space-y-1">
                          {lead.customer?.email && (
                            <div className="flex items-center text-xs text-gray-600">
                              <EnvelopeIcon className="h-3 w-3 mr-1 text-blue-500" />
                              <span className="truncate">{lead.customer.email}</span>
                            </div>
                          )}
                          {lead.customer?.phone && (
                            <div className="flex items-center text-xs text-gray-600">
                              <PhoneIcon className="h-3 w-3 mr-1 text-green-500" />
                              <span>{lead.customer.phone}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-2">
                          <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {formatDate(lead.createdAt)}
                          </div>
                          {lead.updatedAt !== lead.createdAt && (
                            <div className="inline-flex items-center px-2 py-1 rounded-md bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              {formatDate(lead.updatedAt)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell min-w-0 px-3 sm:px-4 py-4 sm:py-6">
                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <EnvelopeIcon className="h-4 w-4 mr-2 text-blue-500" />
                        <span className="text-gray-900 truncate">{lead.customer?.email || 'No Email'}</span>
                      </div>
                      {lead.customer?.phone && (
                        <div className="flex items-center text-sm">
                          <PhoneIcon className="h-4 w-4 mr-2 text-green-500" />
                          <span className="text-gray-900">{lead.customer.phone}</span>
                        </div>
                      )}
                      {lead.customer?.address && (
                        <div className="flex items-center text-sm">
                          <MapPinIcon className="h-4 w-4 mr-2 text-red-500" />
                          <span className="text-gray-600 truncate">
                            {typeof lead.customer.address === 'object' 
                              ? `${lead.customer.address.street || ''} ${lead.customer.address.city || ''} ${lead.customer.address.state || ''}`.trim()
                              : lead.customer.address
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="w-20 sm:w-24 px-3 sm:px-4 py-4 sm:py-6">
                    <div className="p-2 sm:p-3 bg-gradient-to-r from-amber-50/80 to-orange-50/80 rounded-xl text-center">
                      <div className="text-xs sm:text-sm font-bold text-gray-900">{companyData?.currency?.symbol || '$'}{lead.value || 0}</div>
                    </div>
                  </td>
                  <td className="w-24 sm:w-32 px-3 sm:px-4 py-4 sm:py-6">
                    <div className="space-y-2 sm:space-y-3">
                      <select
                        value={(lead.stage && (lead.stage._id || lead.stage)) || ''}
                        onChange={(e) => handleStageChange(lead._id, e.target.value)}
                        className="inline-flex px-2 sm:px-3 py-1 text-xs font-bold rounded-full border-2 transition-all duration-200 text-white w-full sm:w-auto"
                        style={{ backgroundColor: getStageColor(lead.stage) }}
                      >
                        {leadStages.map(stage => (
                          <option key={stage._id} value={stage._id}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
                        <span className={`inline-flex px-2 sm:px-3 py-1 text-xs font-bold rounded-full border-2 ${getPriorityColor(lead.priority)} self-start`}>
                          {lead.priority || 'Not Set'}
                        </span>
                        {lead.status && lead.status !== 'active' && (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${lead.status === 'qualified' ? 'bg-green-100 text-green-800 border border-green-300' : lead.status === 'disqualified' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-gray-100 text-gray-800 border border-gray-300'} self-start`}>
                            {lead.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell w-48 px-3 sm:px-4 py-4 sm:py-6">
                    <div className="relative group">
                      {/* Assignment Display */}
                      <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                        {lead.assignedTo ? (
                          <>
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <UserIcon className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                              </div>
                              <div className="text-xs text-gray-600 truncate">
                                {lead.assignedTo.role || 'Sales Rep'}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                              <UserIcon className="w-4 h-4 text-gray-400" />
                            </div>
                            <div className="text-sm text-gray-500">Unassigned</div>
                          </>
                        )}
                      </div>

                      {/* Assignment Select - Hidden by default, shown on hover */}
                      <div className="absolute top-full left-0 right-0 z-10 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
                        <select
                          value={lead.assignedTo?._id || ''}
                          onChange={(e) => handleAssignmentChange(lead._id, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-lg transition-all duration-200"
                        >
                          <option value="">Unassigned</option>
                          {users.map(user => (
                            <option key={user._id} value={user._id}>
                              {user.firstName} {user.lastName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </td>
                  <td className="hidden lg:table-cell min-w-0 px-3 sm:px-4 py-4 sm:py-6">
                    <div className="space-y-2">
                      {/* Customer Custom Fields */}
                      {lead.customer?.customFields && Object.entries(lead.customer.customFields).map(([key, value]) => (
                        <span
                          key={`customer-${key}`}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300"
                          title={`Customer: ${key}`}
                        >
                          <span className="font-semibold">{key}:</span>
                          <span className="ml-1">{String(value)}</span>
                        </span>
                      ))}
                      {/* Lead Custom Fields */}
                      {lead.customFields && Object.entries(lead.customFields).map(([key, value]) => (
                        <span
                          key={`lead-${key}`}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300"
                          title={`Lead: ${key}`}
                        >
                          <span className="font-semibold">{key}:</span>
                          <span className="ml-1">{String(value)}</span>
                        </span>
                      ))}
                      {(!lead.customer?.customFields || Object.keys(lead.customer.customFields).length === 0) &&
                       (!lead.customFields || Object.keys(lead.customFields).length === 0) && (
                        <span className="text-xs text-gray-400 italic">No custom fields</span>
                      )}
                    </div>
                  </td>
                  
                  <td className="w-40 px-3 sm:px-4 py-4 sm:py-6">
                    <div className="flex items-center justify-center space-x-1">
                      <motion.button
                        whileHover={{ scale: 1.1, backgroundColor: "#EEF2FF" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onViewLead(lead._id)}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-indigo-200"
                        title="View Lead Details"
                      >
                        <EyeIcon className="h-3.5 w-3.5 mr-1" />
                        <span className="hidden sm:inline">View</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1, backgroundColor: "#EFF6FF" }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onEditLead(lead._id)}
                        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-blue-200"
                        title="Edit Lead"
                      >
                        <PencilIcon className="h-3.5 w-3.5 mr-1" />
                        <span className="hidden sm:inline">Edit</span>
                      </motion.button>
                      {hasRole('company_admin') && (
                        <motion.button
                          whileHover={{ scale: 1.1, backgroundColor: "#FEF2F2" }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteLead(lead)}
                          className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-red-200"
                          title="Delete Lead"
                        >
                          <TrashIcon className="h-3.5 w-3.5 mr-1" />
                          <span className="hidden sm:inline">Delete</span>
                        </motion.button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {leads.length === 0 ? (
        <div className="col-span-full text-center py-12 px-4">
          <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
            <UserPlusIcon className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            {searchTerm || selectedStage || selectedPriority || selectedAssignedTo || selectedSource || selectedStatus !== 'active'
              ? 'Try adjusting your filters or search terms to find more leads.'
              : 'Get started by creating your first lead to track potential customers and opportunities.'
            }
          </p>
          <button
            onClick={onCreateLead}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create First Lead
          </button>
        </div>
      ) : (
        leads.map((lead) => (
          <motion.div
            key={lead._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200 overflow-hidden"
          >
            {/* Card Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-sm">
                        {(lead.customer?.firstName || 'N')[0].toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 truncate">
                      {lead.customer?.firstName || 'N/A'} {lead.customer?.lastName || ''}
                    </h3>
                    <p className="text-xs text-gray-600 truncate">
                      {lead.customer?.companyName || 'No Company'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLeads([...selectedLeads, lead._id]);
                      } else {
                        setSelectedLeads(selectedLeads.filter(id => id !== lead._id));
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Card Content */}
            <div className="p-4 space-y-3">
              {/* Value and Stage */}
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-gray-900">
                  {companyData?.currency?.symbol || '$'}{lead.value || 0}
                </div>
                <select
                  value={(lead.stage && (lead.stage._id || lead.stage)) || ''}
                  onChange={(e) => handleStageChange(lead._id, e.target.value)}
                  className="text-xs px-2 py-1 rounded-full border-2 transition-all duration-200 text-white"
                  style={{ backgroundColor: getStageColor(lead.stage) }}
                >
                  {leadStages.map(stage => (
                    <option key={stage._id} value={stage._id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Contact Info */}
              <div className="space-y-2">
                {lead.customer?.email && (
                  <div className="flex items-center text-xs text-gray-600">
                    <EnvelopeIcon className="h-3 w-3 mr-2 text-blue-500" />
                    <span className="truncate">{lead.customer.email}</span>
                  </div>
                )}
                {lead.customer?.phone && (
                  <div className="flex items-center text-xs text-gray-600">
                    <PhoneIcon className="h-3 w-3 mr-2 text-green-500" />
                    <span>{lead.customer.phone}</span>
                  </div>
                )}
              </div>

              {/* Priority and Assignment */}
              <div className="flex items-center justify-between">
                <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full border-2 ${getPriorityColor(lead.priority)}`}>
                  {lead.priority || 'Not Set'}
                </span>
                {lead.assignedTo ? (
                  <div className="flex items-center space-x-1">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <UserIcon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs text-gray-600 truncate max-w-20">
                      {lead.assignedTo.firstName}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">Unassigned</span>
                )}
              </div>

              {/* Assignment Select */}
              <div className="pt-2">
                <select
                  value={lead.assignedTo?._id || ''}
                  onChange={(e) => handleAssignmentChange(lead._id, e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm transition-all duration-200"
                >
                  <option value="">Unassigned</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Created Date */}
              <div className="text-xs text-gray-500">
                Created {formatDate(lead.createdAt)}
              </div>
            </div>

            {/* Card Actions */}
            <div className="px-4 pb-4">
              <div className="flex items-center justify-center space-x-1">
                <motion.button
                  whileHover={{ scale: 1.1, backgroundColor: "#EEF2FF" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onViewLead(lead._id)}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-indigo-200"
                  title="View Lead Details"
                >
                  <EyeIcon className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">View</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1, backgroundColor: "#EFF6FF" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onEditLead(lead._id)}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-blue-200"
                  title="Edit Lead"
                >
                  <PencilIcon className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </motion.button>
                {hasRole('company_admin') && (
                  <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: "#FEF2F2" }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteLead(lead)}
                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md border border-red-200"
                    title="Delete Lead"
                  >
                    <TrashIcon className="h-3.5 w-3.5 mr-1" />
                    <span className="hidden sm:inline">Delete</span>
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        ))
      )}
    </div>
  );

  return (
    <>
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg mb-4">
        <div className="px-4 sm:px-6 py-3">
          <div className="flex flex-col space-y-3">
            {/* Main Filter Row */}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
              {/* Search */}
              <div className="flex-1 min-w-0 max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-gray-50/50"
                  />
                </div>
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white min-w-[120px]"
                >
                  <option value="">All Stages</option>
                  {leadStages.map(stage => (
                    <option key={stage._id} value={stage._id}>
                      {stage.name}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white min-w-[130px]"
                >
                  <option value="">All Priorities</option>
                  {priorityOptions.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>

                <select
                  value={dateFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDateFilter(value);
                    if (value !== 'custom') {
                      setStartDate('');
                      setEndDate('');
                      setShowCustomDatePicker(false);
                    } else {
                      setShowCustomDatePicker(true);
                    }
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white min-w-[140px]"
                >
                  <option value="">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="this_week">This Week</option>
                  <option value="last_week">Last Week</option>
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_year">This Year</option>
                  <option value="last_year">Last Year</option>
                  <option value="custom">Custom Range</option>
                </select>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center px-3 py-2.5 border shadow-sm text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    showFilters
                      ? 'border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100'
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  Filters
                  {showFilters ? (
                    <ChevronUpIcon className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 ml-2" />
                  )}
                </button>

                {/* Cost Analysis Toggle */}
                <div className="flex items-center border-l border-gray-300 pl-3 ml-3">
                  <button
                    onClick={() => setShowCostAnalysis(!showCostAnalysis)}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                      showCostAnalysis
                        ? 'text-white bg-emerald-600 shadow-sm hover:bg-emerald-700'
                        : 'text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title={showCostAnalysis ? 'Hide cost analysis' : 'Show cost analysis - View lead value breakdown by stages'}
                  >
                    {showCostAnalysis ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* View Toggle */}
                <div className="flex items-center border-l border-gray-300 pl-3 ml-3">
                  <div className="flex rounded-lg border border-gray-300 bg-gray-50 p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                        viewMode === 'table'
                          ? 'text-white bg-indigo-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Bars3Icon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('kanban')}
                      className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                        viewMode === 'kanban'
                          ? 'text-white bg-indigo-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Squares2X2Icon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Date Range - Compact */}
            <AnimatePresence>
              {showCustomDatePicker && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="flex flex-wrap items-end gap-3 pt-2 border-t border-gray-100"
                >
                  <div className="min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  </div>
                  <div className="min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                  </div>
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4 mr-2" />
                    Clear All
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Advanced Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="pt-3 border-t border-gray-100"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
                      <select
                        value={selectedAssignedTo}
                        onChange={(e) => setSelectedAssignedTo(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                      >
                        <option value="">All Users</option>
                        {users.map(user => (
                          <option key={user._id} value={user._id}>
                            {user.firstName} {user.lastName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
                      <select
                        value={selectedSource}
                        onChange={(e) => setSelectedSource(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                      >
                        <option value="">All Sources</option>
                        {sourceOptions.map(source => (
                          <option key={source} value={source}>
                            {source}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
                      >
                        {statusOptions.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={clearFilters}
                        className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4 mr-2" />
                        Clear Filters
                      </button>
                    </div>
                  </div>

                  {/* Custom Fields Filters */}
                  {customFields.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-800 mb-3">Custom Fields</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {customFields.map((field) => (
                          <div key={field._id} className="space-y-1">
                            <label className="block text-xs font-medium text-gray-600">
                              {field.fieldName || 'Field'}
                            </label>
                            {renderCustomFieldFilter(field)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Cost Analysis Section */}
      {showCostAnalysis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ delay: 0.2 }}
          className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden mb-6"
        >
          <div className="relative overflow-hidden bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-6 py-4">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/30 via-teal-400/30 to-cyan-400/30"></div>
            <div className="relative flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm border border-white/30">
                <Bars3Icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">💰 Lead Cost Analysis</h2>
                <p className="text-emerald-100 text-sm">Cost breakdown by lead stages</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Cost */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-4 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-slate-200 rounded-lg">
                    <Bars3Icon className="h-5 w-5 text-slate-600" />
                  </div>
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Total Cost</span>
                </div>
                <div className="text-2xl font-bold text-slate-900 mb-1">
                  {companyData?.currency?.symbol || '$'}{costAnalysis.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-slate-600">
                  All leads in current filter
                </div>
              </motion.div>

              {/* Won Cost */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-4 border border-green-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-green-200 rounded-lg">
                    <UserIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Won Cost</span>
                </div>
                <div className="text-2xl font-bold text-green-900 mb-1">
                  {companyData?.currency?.symbol || '$'}{costAnalysis.wonCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-green-600">
                  Converted leads value
                </div>
              </motion.div>

              {/* In Process Cost */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-4 border border-blue-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-blue-200 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">In Process</span>
                </div>
                <div className="text-2xl font-bold text-blue-900 mb-1">
                  {companyData?.currency?.symbol || '$'}{costAnalysis.inProcessCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-blue-600">
                  Active pipeline value
                </div>
              </motion.div>

              {/* Lost Cost */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
                className="bg-gradient-to-br from-red-50 to-rose-100 rounded-2xl p-4 border border-red-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-red-200 rounded-lg">
                    <XMarkIcon className="h-5 w-5 text-red-600" />
                  </div>
                  <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Lost Cost</span>
                </div>
                <div className="text-2xl font-bold text-red-900 mb-1">
                  {companyData?.currency?.symbol || '$'}{costAnalysis.lostCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-red-600">
                  Lost opportunity value
                </div>
              </motion.div>
            </div>

            {/* Summary Stats */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="mt-6 pt-4 border-t border-gray-200"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {((costAnalysis.wonCost / costAnalysis.totalCost) * 100 || 0).toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wide">Win Rate</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {((costAnalysis.lostCost / costAnalysis.totalCost) * 100 || 0).toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wide">Loss Rate</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {((costAnalysis.inProcessCost / costAnalysis.totalCost) * 100 || 0).toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wide">Pipeline %</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {leads.length}
                  </div>
                  <div className="text-xs text-gray-600 uppercase tracking-wide">Total Leads</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Bulk Actions */}
      {selectedLeads.length > 0 && hasRole('company_admin') && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center">
              <span className="text-sm text-indigo-700">
                {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
              <button
                onClick={handleBulkDelete}
                className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {viewMode === 'table' ? renderTableView() : renderCardView()}

          {quickFollowupOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add Followup for {quickFollowupLead?.customer?.firstName || 'N/A'} {quickFollowupLead?.customer?.lastName || ''}</h3>
                  <div className="space-y-3">
                    <textarea value={quickFollowupData.note} onChange={(e) => setQuickFollowupData(prev => ({ ...prev, note: e.target.value }))} className="w-full border px-3 py-2 rounded-md" rows={4} placeholder="Note" />
                    <input type="datetime-local" value={quickFollowupData.remindAt} onChange={(e) => setQuickFollowupData(prev => ({ ...prev, remindAt: e.target.value }))} className="w-full border px-3 py-2 rounded-md" />
                    <div className="flex items-center justify-end space-x-3">
                      <button onClick={() => setQuickFollowupOpen(false)} className="px-3 py-2 bg-gray-100 rounded-md">Cancel</button>
                      <button onClick={async () => {
                        if (!quickFollowupData.note.trim() && !quickFollowupData.remindAt) { toast.error('Add a note or reminder'); return; }
                        try {
                          const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')||'{}'); } catch { return {}; } })();
                          const assignedTo = quickFollowupLead?.assignedTo?._id || currentUser._id;
                          const scheduledDate = quickFollowupData.remindAt ? new Date(quickFollowupData.remindAt).toISOString() : new Date().toISOString();
                          const title = quickFollowupData.note ? (quickFollowupData.note.length > 80 ? quickFollowupData.note.slice(0,80) + '...' : quickFollowupData.note) : `Followup for ${quickFollowupLead?.customer?.firstName || 'N/A'} ${quickFollowupLead?.customer?.lastName || ''}`;

                          await followupsAPI.create({
                            title,
                            description: quickFollowupData.note,
                            type: 'reminder',
                            priority: 'medium',
                            scheduledDate,
                            duration: 30,
                            assignedTo,
                            relatedTo: 'lead',
                            relatedId: quickFollowupLead._id,
                            notes: quickFollowupData.note
                          });

                          toast.success('Followup created');
                          setQuickFollowupOpen(false);
                          fetchLeads();
                        } catch (err) { console.error('Quick followup error', err); toast.error('Failed to create followup'); }
                      }} className="px-3 py-2 bg-indigo-600 text-white rounded-md">Save</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 mt-6">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalLeads)} of {totalLeads} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default LeadList;
